import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { GetWorkoutTemplateNameFromKey } from 'functions/textUtils'
import moment from 'moment'

import ContentFrame from 'components/Frame/ContentFrame'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import SolidButton from 'components/Buttons/SolidButton'

export default function DefaultWeeklyScheduleTab() {

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)

    const [weekDaySchedule, setWeekDaySchedule] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [isSaveChangesLoading, setIsSaveChangesLoading] = useState(false)
    const [isWeekDayScheduleValid, setIsWeekDayScheduleValid] = useState(true)

    const GetDefaultWeeklyScheduleFromBackend = async () => {

        if (profileType === 'group') {
            const jsonBody = {
                groupId: groupId
            }
    
            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'GET', '/default_group_week_day_schedule_json', jsonBody)
    
            // console.log(result)
    
            if (result.data.status === 'failure') {
                console.error(result)
                return
            }
    
            setWeekDaySchedule(result.data.data)
        }

        if (profileType === 'individual') {
            const jsonBody = {
                userId: athleteId
            }
    
            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'GET', '/default_week_day_schedule_json', jsonBody)
    
            // console.log(result)
    
            if (result.data.status === 'failure') {
                console.error(result)
                return
            }
    
            setWeekDaySchedule(result.data.data)
        }

    }

    const HandleWeekDayScheduleChange = (weekDayIndex, workoutTemplateKey) => {
        setIsWeekDayScheduleValid(true)
        const tempWeekDaySchedule = JSON.parse(JSON.stringify(weekDaySchedule))

        // Remove all instances of the workout template key from the schedule array first
        Object.keys(tempWeekDaySchedule).map((key) => {
            if (tempWeekDaySchedule[key] === workoutTemplateKey) tempWeekDaySchedule[key] = 'rest'
        })

        tempWeekDaySchedule[weekDayIndex] = workoutTemplateKey
        setWeekDaySchedule(tempWeekDaySchedule)
    }

    const OnSaveChangesButton = async () => {
        // Verifying input
        const assignedWorkoutTemplateKeys = Object.values(weekDaySchedule)

        if (
            !assignedWorkoutTemplateKeys.includes('strength-session-a') ||
            !assignedWorkoutTemplateKeys.includes('strength-session-b') ||
            !assignedWorkoutTemplateKeys.includes('mobility-a') ||
            !assignedWorkoutTemplateKeys.includes('mobility-b')
        ) {
            setIsWeekDayScheduleValid(false)
            return
        }

        setIsSaveChangesLoading(true)

        if (profileType === 'group') {
            const jsonBody = {
                groupId: groupId,
                weekDaySchedule: weekDaySchedule,
            }

            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'POST', '/default_group_week_day_schedule_json', jsonBody)
            
            // console.log(result)

            setIsSaveChangesLoading(false)
    
            if (result.data.status === 'success') {
                setIsEditing(false)
                window.location.reload()
            }
        }

        if (profileType === 'individual') {
            const jsonBody = {
                userId: athleteId,
                weekDaySchedule: weekDaySchedule,
            }

            // console.log(jsonBody)
    
            const result = await WhichWeightAPI('PROGRAM', 'POST', '/default_week_day_schedule_json', jsonBody)

            // console.log(result)
            
            setIsSaveChangesLoading(false)
    
            if (result.data.status === 'success') {
                setIsEditing(false)
                window.location.reload()
            }
        }
        
    }

    useEffect(() => { GetDefaultWeeklyScheduleFromBackend() }, [])

    if (weekDaySchedule === null) {
        return (
            <div className='w-full h-80 bg-gray-200 rounded-lg animate-pulse' />
        )
    }

    if (isEditing === false) {
        return (
            <>
                <div className="flex items-center">
                    <div className="flex-1">Default Weekly Schedule</div>

                    <div className="w-32">
                        <BrandColorButton
                            size='extra-small'
                            text="Edit"
                            action={() => setIsEditing(true)}
                            className="shadow-md"
                        />
                    </div>
                </div>

                <ContentFrame>
                    <div className="w-full p-4 space-y-4">
                        <div className="space-y-2">
                            {Array.from({ length: 7 }, (_, i) => i).map((weekDayIndex) => (
                                <div
                                    key={weekDayIndex}
                                    className="flex items-center gap-4 py-2 px-3 rounded-lg"
                                >
                                    <div className="flex-grow">
                                        {moment.utc().local().startOf('isoWeek').add(weekDayIndex, 'days').format('dddd')}
                                    </div>

                                    <div className="w-64 h-8 text-right text-gray-600">
                                        {GetWorkoutTemplateNameFromKey(weekDaySchedule[weekDayIndex])}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </ContentFrame>
            </>
        )
    }

    if (isEditing === true) {
        return (
            <>
                <div className="flex items-center">
                    <div className="flex-1">Default Weekly Schedule</div>

                    <div className='flex items-center gap-x-2'>
                        <div className="w-32">
                            <SolidButton
                                size='extra-small'
                                text="Cancel"
                                action={() => setIsEditing(false)}
                                className="shadow-md"
                            />
                        </div>

                        <div className="w-32">
                            <BrandColorButton
                                size='extra-small'
                                text="Save Changes"
                                isLoading={isSaveChangesLoading}
                                action={OnSaveChangesButton}
                                className="shadow-md"
                            />
                        </div>
                    </div>
                </div>

                <ContentFrame>
                    <div className="w-full p-4 space-y-4">
                        <div className="space-y-2">
                            {Array.from({ length: 7 }, (_, i) => i).map((weekDayIndex) => (
                                <div
                                    key={weekDayIndex}
                                    className="flex items-center gap-4 py-2 px-3"
                                >
                                    <div className="flex-grow">
                                        {moment.utc().local().startOf('isoWeek').add(weekDayIndex, 'days').format('dddd')}
                                    </div>

                                    <div className="h-8 border border-gray-300 rounded-md cursor-pointer">
                                        <select
                                            value={weekDaySchedule[weekDayIndex]}
                                            onChange={(event) => HandleWeekDayScheduleChange(weekDayIndex, event.target.value)}
                                            className="h-full w-64 px-4 text-gray-900 rounded-md border-r-[16px] border-white"
                                        >
                                            <option value="strength-session-a">Strength Session A</option>
                                            <option value="strength-session-b">Strength Session B</option>
                                            <option value="mobility-a">Mobility A</option>
                                            <option value="mobility-b">Mobility B</option>
                                            <option value="rest">Rest</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {!isWeekDayScheduleValid && (
                            <p className="mt-2 text-sm text-red-600">Please make sure all 4 workouts have been assigned a day of the week</p>
                        )}

                        <p className="text-sm text-center text-gray-500 italic">
                            Changes to the will not effect the present week schedule. Will only go into effect for future weeks.
                        </p>
                    </div>
                </ContentFrame>
            </>
        )
    }
}